import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Award,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  Download,
  RefreshCw,
  TrendingUp,
  Shield,
  BarChart3,
  Clock,
  Sparkles,
  Lightbulb
} from "lucide-react";
import { toast } from "sonner";

export default function ComplianceDashboardPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [complianceData, setComplianceData] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState("iso_22301");
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiGapAnalysis, setAiGapAnalysis] = useState(null);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      setLoading(true);
      // Real, computed compliance status (BCM lifecycle areas PP1–PP6) from the backend.
      const statusRes = await api.get("/compliance/status");
      const s = statusRes.data;
      if (!s || typeof s !== "object") {
        setComplianceData({ summary: null, report: null });
        setLoadError(true);
        return;
      }
      const areas = [
        { id: "PP1", name: "Policy & Programme Management", ...s.pp1_policy },
        { id: "PP2", name: "Embedding & Training", ...s.pp2_training },
        { id: "PP3a", name: "Business Impact Analysis", ...s.pp3_bia },
        { id: "PP3b", name: "Risk Assessment", ...s.pp3_risk },
        { id: "PP4", name: "Strategy & Design", ...s.pp4_strategy },
        { id: "PP5", name: "Plans", ...s.pp5_plans },
        { id: "PP6", name: "Exercises & Validation", ...s.pp6_exercises },
      ];
      const clauses = areas.map(a => ({
        id: a.id,
        name: a.name,
        score: a.status === "not_assessed" ? null : Math.round(a.score || 0),
        status: a.status || "not_assessed",
      }));
      const overall = (s.overall_score === null || s.overall_score === undefined) ? null : Math.round(s.overall_score);
      const overallStatus = overall === null
        ? "not_assessed"
        : (overall >= 80 ? "compliant" : overall >= 60 ? "needs_attention" : "critical");
      const summary = {
        overall_score: overall,
        status: overallStatus,
        last_audit: "—",
        next_audit: "—",
        metrics: {
          policies_approved: { current: s.pp1_policy?.approved_policies || 0, total: s.pp1_policy?.total_policies || 0 },
          bia_completed: { current: s.pp3_bia?.total_assessments || 0, total: s.pp3_bia?.total_assessments || 0 },
          plans_tested: { current: s.pp6_exercises?.completed_exercises || 0, total: s.pp6_exercises?.total_exercises || 0 },
          training_completed: { current: s.pp2_training?.users_trained || 0, total: s.pp2_training?.total_users || 0 },
        },
        frameworks: {
          iso_22301: {
            name: "BCM Compliance",
            score: overall,
            compliant: clauses.filter(c => c.status === "compliant").length,
            partial: clauses.filter(c => c.status === "partial").length,
            non_compliant: clauses.filter(c => c.status === "non_compliant").length,
            clauses,
          },
        },
        recommendations: s.recommendations || [],
      };
      setComplianceData({ summary, report: null });
      setLoadError(false);
    } catch (error) {
      // No fabricated score: show an honest error state instead.
      setComplianceData({ summary: null, report: null });
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      compliant: { variant: "default", className: "bg-emerald-500", icon: CheckCircle2, label: "Compliant" },
      partial: { variant: "default", className: "bg-amber-500", icon: AlertTriangle, label: "Partial" },
      non_compliant: { variant: "destructive", icon: XCircle, label: "Non-Compliant" },
      not_assessed: { variant: "outline", className: "bg-slate-100 text-slate-500 border-slate-300", icon: null, label: "Not Assessed" }
    };
    const cfg = config[status] || config.not_assessed;
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className={cfg.className}>
        {Icon && <Icon className="w-3 h-3 mr-1" />}
        {cfg.label}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const handleExportReport = async () => {
    try {
      const response = await api.get("/compliance/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `compliance_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  // AI-powered compliance gap analysis - Now using real backend
  const handleAIGapAnalysis = async () => {
    setAiAnalyzing(true);
    try {
      const response = await api.post("/ai/compliance-gap-analysis", {
        overall_score: summary?.overall_score,
        compliance_data: summary || {}
      });
      if (response.data.success) {
        setAiGapAnalysis(response.data.data);
        toast.success("AI gap analysis completed");
      }
    } catch (error) {
      toast.error("AI analysis failed: " + (extractErrorMessage(error, "Unknown error")));
    } finally {
      setAiAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Honest failure: never render an invented compliance score / clause breakdown / counts.
  if (loadError || !complianceData?.summary) {
    return (
      <div className="space-y-6">
        {!embedded && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
              <Badge variant="outline">ISO 22301 Clause 9.1</Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Compliance Dashboard</h1>
          </div>
        )}
        <div className="border border-red-200 bg-red-50 rounded-lg p-8 text-center" data-testid="compliance-load-error">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-500" />
          <p className="font-semibold text-red-800">Compliance data could not be loaded</p>
          <p className="text-sm text-red-700 mt-1">No score, clause breakdown, or compliant/partial/non-compliant counts are shown — this is not sample data. The figures could not be retrieved.</p>
          <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-100" onClick={fetchComplianceData} data-testid="compliance-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const summary = complianceData?.summary;
  const framework = summary?.frameworks?.[selectedFramework];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded && (
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 9.1</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Compliance Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor your organization's compliance status and track improvements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchComplianceData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            onClick={handleAIGapAnalysis}
            disabled={aiAnalyzing}
            className="text-purple-600 border-purple-200"
            data-testid="ai-gap-analysis-btn"
          >
            {aiAnalyzing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            AI Gap Analysis
          </Button>
          <Button onClick={handleExportReport}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
      )}

      {/* Overall Score Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="relative">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                  <circle 
                    cx="48" cy="48" r="40" 
                    fill="none" 
                    stroke={summary?.overall_score === null ? "#cbd5e1" : summary?.overall_score >= 80 ? "#10b981" : summary?.overall_score >= 60 ? "#f59e0b" : "#ef4444"}
                    strokeWidth="8"
                    strokeDasharray={`${((summary?.overall_score || 0) / 100) * 251.2} 251.2`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  {summary?.overall_score === null ? (
                    <span className="text-xs font-semibold text-slate-400 text-center px-1">Not Assessed</span>
                  ) : (
                    <span className={`text-2xl font-bold ${getScoreColor(summary?.overall_score)}`}>
                      {summary?.overall_score}%
                    </span>
                  )}
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Overall Compliance Score</h2>
                <p className="text-slate-500">Based on BCM lifecycle areas (PP1–PP6)</p>
                {(() => {
                  const cl = summary?.frameworks?.iso_22301?.clauses || [];
                  const assessed = cl.filter(c => c.status !== 'not_assessed').length;
                  return cl.length ? (
                    <p className="text-xs text-slate-400 mt-0.5" data-testid="assessed-areas-note">
                      Based on {assessed} of {cl.length} assessed areas
                    </p>
                  ) : null;
                })()}
                <Badge 
                  variant={summary?.status === "compliant" ? "default" : "secondary"}
                  className={summary?.status === "compliant" ? "bg-emerald-500 mt-2" : "mt-2"}
                >
                  {summary?.status === "not_assessed" ? "Not Assessed" :
                   summary?.status === "compliant" ? "Fully Compliant" : 
                   summary?.status === "needs_attention" ? "Needs Attention" : "Critical Issues"}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Clock className="w-4 h-4" />
                Last Audit: {summary?.last_audit}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                Next Audit: {summary?.next_audit}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Policies Approved</span>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{summary?.metrics?.policies_approved?.current}</span>
              <span className="text-slate-400">/ {summary?.metrics?.policies_approved?.total}</span>
            </div>
            <Progress 
              value={(summary?.metrics?.policies_approved?.current / summary?.metrics?.policies_approved?.total) * 100} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">BIA Completed</span>
              <BarChart3 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{summary?.metrics?.bia_completed?.current}</span>
              <span className="text-slate-400">/ {summary?.metrics?.bia_completed?.total}</span>
            </div>
            <Progress 
              value={(summary?.metrics?.bia_completed?.current / summary?.metrics?.bia_completed?.total) * 100} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Plans Tested</span>
              <Shield className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{summary?.metrics?.plans_tested?.current}</span>
              <span className="text-slate-400">/ {summary?.metrics?.plans_tested?.total}</span>
            </div>
            <Progress 
              value={(summary?.metrics?.plans_tested?.current / summary?.metrics?.plans_tested?.total) * 100} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Training Completed</span>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{summary?.metrics?.training_completed?.current}</span>
              <span className="text-slate-400">/ {summary?.metrics?.training_completed?.total}</span>
            </div>
            <Progress 
              value={(summary?.metrics?.training_completed?.current / summary?.metrics?.training_completed?.total) * 100} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>
      </div>

      {/* Framework Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            BCM Compliance by Area
          </CardTitle>
          <CardDescription>Detailed status for each BCM lifecycle area (PP1–PP6)</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Area</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {framework?.clauses?.map((clause) => (
                <TableRow key={clause.id} data-testid={`clause-${clause.id}`}>
                  <TableCell className="font-medium">{clause.id}</TableCell>
                  <TableCell>{clause.name}</TableCell>
                  <TableCell>
                    {clause.status === "not_assessed" || clause.score === null ? (
                      <span className="font-semibold text-slate-400">—</span>
                    ) : (
                      <span className={`font-semibold ${getScoreColor(clause.score)}`}>
                        {clause.score}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(clause.status)}</TableCell>
                  <TableCell>
                    {clause.status === "not_assessed" || clause.score === null ? (
                      <div className="w-24 h-2 rounded-full bg-slate-100" />
                    ) : (
                      <Progress value={clause.score || 0} className="w-24 h-2" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Improvement Recommendations</CardTitle>
          <CardDescription>Actions to improve your compliance score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {framework?.clauses?.filter(c => c.status !== 'compliant').map((clause) => (
              <div key={clause.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <AlertTriangle className={`w-5 h-5 mt-0.5 ${clause.status === 'non_compliant' ? 'text-red-500' : clause.status === 'not_assessed' ? 'text-slate-400' : 'text-amber-500'}`} />
                <div>
                  <p className="font-medium text-slate-900">{clause.id}: {clause.name}</p>
                  <p className="text-sm text-slate-600">
                    {clause.status === 'not_assessed'
                      ? 'Not assessed yet — no records captured in this area. Add records to begin measuring.'
                      : clause.status === 'non_compliant'
                      ? 'Critical: Requires immediate attention to meet compliance requirements.'
                      : 'Partially compliant: Some improvements needed to reach full compliance.'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Gap Analysis Results */}
      {aiGapAnalysis && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <Sparkles className="w-5 h-5" />
              AI-Powered Gap Analysis
            </CardTitle>
            <CardDescription>Intelligent recommendations based on your compliance data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="p-4 bg-white rounded-lg border">
                <p className="text-slate-700">{aiGapAnalysis.summary}</p>
              </div>

              {/* Priority Actions */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Priority Actions
                </h4>
                <div className="space-y-3">
                  {aiGapAnalysis.priority_actions?.map((action, idx) => (
                    <div key={idx} className="p-3 bg-white rounded-lg border flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{action.clause}</p>
                        <p className="text-sm text-slate-600 mt-1">{action.action}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">Impact: {action.impact}</Badge>
                          <Badge variant="outline" className="text-xs">Effort: {action.effort}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estimated Improvement */}
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg">
                <p className="text-sm text-emerald-800 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  {aiGapAnalysis.estimated_improvement}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
